'use client'

import clsx from 'clsx'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { BannerLocaleResult } from '../data/remote/BannerFragment'
import type { MailingListLocaleResult } from '../data/remote/MailingListFragment'
import { FrontPageSideBanner } from './FrontPageSideBanner'
import styles from './FrontPageCombinedBanner.module.sass'

export type FrontPageCombinedBannerProps = {
	banners: BannerLocaleResult[]
	mailingList: MailingListLocaleResult
}

const BannerImage = ({
	image,
	className,
}: {
	image?: { url: string; alt?: string }
	className?: string
}) => {
	if (!image?.url) return null

	return (
		<Image
			className={clsx(styles.image, className)}
			src={image.url}
			fill
			alt={image.alt ?? ''}
			sizes="(min-width: 75rem) 40vw, 100vw"
		/>
	)
}

const BannerImageSet = ({ banner }: { banner: BannerLocaleResult }) => (
	<>
		<div className={clsx(styles.imageWrapper, styles.imageWrapperDesktop)}>
			<BannerImage image={banner.narrowImage} />
		</div>
		<div className={clsx(styles.imageWrapper, styles.imageWrapperMobile)}>
			<BannerImage image={banner.wideImage} />
		</div>
	</>
)

export const FrontPageCombinedBanner: FunctionComponent<FrontPageCombinedBannerProps> = (props) => {
	const { banners, mailingList } = props
	const [currentIndex, setCurrentIndex] = useState(banners.length > 0 ? 1 : 0) // Start at 1 if we have clones
	const [isTransitioning, setIsTransitioning] = useState(true)
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const slidesRef = useRef<HTMLDivElement>(null)
	
	// Create array with clones for infinite loop
	const allSlides = banners.length > 0 ? [
		{ type: 'banner' as const, banner: banners[banners.length - 1], isClone: true }, // Clone of last banner
		{ type: 'mailingList' as const, mailingList, isClone: false },
		...banners.map(banner => ({ type: 'banner' as const, banner, isClone: false })),
		{ type: 'mailingList' as const, mailingList, isClone: true }, // Clone of first slide (mailing list)
	] : [
		{ type: 'mailingList' as const, mailingList, isClone: false },
	]
	
	const totalOriginalSlides = banners.length + 1

	useEffect(() => {
		if (totalOriginalSlides > 1) {
			startResetInterval()
		}
		return () => removeInterval()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalOriginalSlides])

	useEffect(() => {
		// Only handle infinite loop transitions if we have multiple slides with clones
		if (banners.length === 0) return
		
		// Handle infinite loop transitions
		if (!isTransitioning) {
			setTimeout(() => {
				setIsTransitioning(true)
			}, 50)
		}
		
		// When reaching clones, reset position
		if (currentIndex === 0) {
			setTimeout(() => {
				setIsTransitioning(false)
				setCurrentIndex(totalOriginalSlides)
			}, 500) // Match transition duration
		} else if (currentIndex === allSlides.length - 1) {
			setTimeout(() => {
				setIsTransitioning(false)
				setCurrentIndex(1)
			}, 500)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentIndex, isTransitioning, totalOriginalSlides, allSlides.length, banners.length])

	const removeInterval = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
		}
	}

	const startResetInterval = () => {
		removeInterval()
		intervalRef.current = setInterval(() => {
			handleNextSlide()
		}, 8000)
	}

	const handlePrevSlide = () => {
		startResetInterval()
		setCurrentIndex(prev => prev - 1)
	}

	const handleNextSlide = () => {
		startResetInterval()
		setCurrentIndex(prev => prev + 1)
	}
	
	const handleDotClick = (index: number) => {
		startResetInterval()
		setCurrentIndex(index + 1) // +1 because of clone at beginning
	}
	
	// Calculate actual slide index for dots (0-based, without clones)
	const actualSlideIndex = currentIndex <= 0 ? totalOriginalSlides - 1 : 
		currentIndex >= allSlides.length - 1 ? 0 : 
		currentIndex - 1

	return (
		<div className={styles.carouselWrapper}>
			{totalOriginalSlides > 1 && (
				<>
					<button type="button" onClick={handlePrevSlide} className={styles.prevButton} aria-label="Previous slide">
						<ChevronLeftIcon className={styles.iconLeft} />
					</button>
					<button type="button" onClick={handleNextSlide} className={styles.nextButton} aria-label="Next slide">
						<ChevronRightIcon className={styles.iconRight} />
					</button>
				</>
			)}
			<div className={styles.slidesContainer} ref={slidesRef}>
				<div 
					className={styles.slideTrack}
					style={{
						transform: `translateX(-${currentIndex * 100}%)`,
						transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
					}}
				>
					{allSlides.map((slide, index) => (
						<div key={`slide_${index}_${slide.isClone ? 'clone' : 'original'}`} className={styles.slide}>
							{slide.type === 'mailingList' ? (
								<div className={styles.mailingListWrapper}>
									<FrontPageSideBanner list={slide.mailingList} />
								</div>
							) : (
								slide.banner.url ? (
									<Link href={slide.banner.url} target="_blank" className={styles.wrapper}>
										<BannerImageSet banner={slide.banner} />
									</Link>
								) : (
									<div className={styles.wrapper}>
										<BannerImageSet banner={slide.banner} />
									</div>
								)
							)}
						</div>
					))}
				</div>
			</div>
			{totalOriginalSlides > 1 && (
				<div className={styles.dots}>
					{Array.from({ length: totalOriginalSlides }).map((_, index) => (
						<button
							key={index}
							type="button"
							onClick={() => handleDotClick(index)}
							className={`${styles.dot} ${index === actualSlideIndex ? styles.activeDot : ''}`}
						/>
					))}
				</div>
			)}
		</div>
	)
}
